import React, { useState } from "react"
import { navigate } from "gatsby"
import { useFormik } from "formik"
import axios from "axios"
import * as Yup from "yup"
import { useToasts } from "react-toast-notifications"
import Form from "react-bootstrap/Form"

import FormError from "../formError"

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .min(4, "Must enter a valid name")
    .required("Must enter your full name"),
  email_from: Yup.string()
    .email("Must be a valid email address")
    .required("Must enter an email"),
  partner_phone: Yup.string().matches(
    /^[0-9]{11}$/,
    "Must be exactly 11 digits"
  ),
  linkedin_profile: Yup.string().required("Must enter a linkedin profile link"),
  relevant_links: Yup.string().min(
    4,
    "Must enter valid links separated by comma"
  ),
  years_of_experience: Yup.string().required("Experience is required"),
  age: Yup.string().required("Age is required"),
  gender: Yup.string().required(),
  disability_inquiry: Yup.string().required(),
  geo_political_zone: Yup.string().required(),
  availability: Yup.string().required("Must choose availability"),
})

const ApplicationForm = (props) => {
  const [error, setError] = useState("")
  const [isUploading, setIsUploading] = useState(false)
  const [attachment, setAttachment] = useState({
    name: "",
    type: "binary",
    datas: "",
    res_model: "hr.applicant",
    res_id: "",
  })

  const { addToast } = useToasts()

  // send attachment after applying
  const sendAttachment = (id) => {
    axios({
      method: "post",
      url: `${process.env.SPARKLE_CAREERS_URL}/ir.attachment/create?api_key=${process.env.SPARKLE_CAREERS}`,
      data: JSON.stringify({
        name: attachment.name,
        datas: attachment.datas,
        res_model: "hr.applicant",
        res_id: id,
        type: "binary",
      }),
      headers: {
        "Content-Type": "text/plain",
      },
    })
      .then((response) => {
        if (response.data.success === false) {
          setError(response.data.error.message)
          addToast(response.data.error.message, {
            appearance: "error",
            placement: "top-center",
            autoDismiss: true,
          })
        } else {
          setIsUploading(false)
          addToast("Application Successful", {
            appearance: "success",
            placement: "top-center",
            autoDismiss: true,
          })
          setTimeout(function () {
            navigate("/careers")
          }, 3000)
          formik.handleReset()
        }
      })
      .catch((request_error) => {
        console.error(request_error)
      })
  }

  const apply = async (data) => {
    setIsUploading(true)
    await axios({
      method: "post",
      url: `${process.env.GATSBY_SPARKLE_LOCAL_BASE_URL}/api/v1/career`,
      data,
      headers: {
        "Content-Type": "text/plain",
      },
    })
      .then((response) => {
        if (response.data.success === false) {
          setError(response.data.error.message)
           setIsUploading(false)
          addToast(response.data.error.message, {
            appearance: "error",
            placement: "top-center",
            autoDismiss: true,
          })
        } else {
          // If successful, add attachment and send to oddo with application id
          sendAttachment(response.data.create_id)
        }
      })
      .catch((request_error) => {
         setIsUploading(false)
        console.error(request_error)
      })
  }

  function handleImage(evt) {
    var reader = new FileReader()

    var file = evt.target.files[0]

    // Convert the file to base64 text
    reader.readAsDataURL(file)

    reader.onload = function () {
      setAttachment({
        ...attachment,
        datas: reader.result.split(",")[1],
        name: file.name,
      })
    }
  }

  const formik = useFormik({
    initialValues: {
      name: "",
      email_from: "",
      partner_phone: "",
      job_id: parseInt(props.id),
      source_id: parseInt(6),
      linkedin_profile: "",
      relevant_links: "",
      years_of_experience: "",
      age: "Prefer not to say",
      gender: "Prefer not to say",
      disability_inquiry: "Prefer not to say",
      geo_political_zone: "Prefer not to say",
      availability: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      formik.setSubmitting(true)
      await apply(JSON.stringify(values))
    },
  })

  return (
    <>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-9">
            <div className="px-lg-4 px-2 py-lg-5 sparkle--color__light mt-lg-3">
              <div onClick={() => navigate(-1)} className="d-flex cursor mt-3">
                <svg
                  width="25"
                  height="23"
                  viewBox="0 0 27 26"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0.762563 11.7626C0.0791457 12.446 0.0791457 13.554 0.762563 14.2374L11.8995 25.3744C12.5829 26.0578 13.691 26.0578 14.3744 25.3744C15.0578 24.691 15.0578 23.5829 14.3744 22.8995L4.47487 13L14.3744 3.1005C15.0578 2.41709 15.0578 1.30905 14.3744 0.625631C13.691 -0.057786 12.5829 -0.057786 11.8995 0.625631L0.762563 11.7626ZM27 11.25H2V14.75H27V11.25Z"
                    fill="#5A7184"
                  />
                </svg>

                <h4 className="sparkle--mont__900 mb-0 mx-3">
                  Back to Position Information
                </h4>
              </div>
              <hr />
              <p className="text-danger">{error}</p>
              <form
                className={
                  isUploading
                    ? "progress_state waitlist__form mt-lg-5 mt-3 px-lg-5"
                    : "waitlist__form mt-lg-5 mt-3 px-lg-5"
                }
                onSubmit={formik.handleSubmit}
              >
                <div className="form-row justify-content-center">
                  <div className="form-group col-md-6">
                    <label htmlFor="name">Full Name</label>
                    <input
                      type="text"
                      className="form-control"
                      autoComplete="name"
                      id="name"
                      name="name"
                      placeholder="Full Name"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.name}
                    />
                    <FormError
                      touched={formik.touched.name}
                      message={formik.errors.name}
                    />
                  </div>

                  <div className="form-group col-md-6">
                    <label htmlFor="email_from">Email</label>
                    <input
                      type="email"
                      autoComplete="email"
                      className="form-control"
                      id="email_from"
                      name="email_from"
                      placeholder="Email"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.email_from}
                    />

                    <FormError
                      touched={formik.touched.email_from}
                      message={formik.errors.email_from}
                    />
                  </div>
                </div>

                <div className="form-row justify-content-center">
                  <div className="form-group col-md-6">
                    <label htmlFor="partner_phone">Phone</label>

                    <input
                      type="text"
                      className="form-control"
                      id="partner_phone"
                      name="partner_phone"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.partner_phone}
                    />

                    <FormError
                      touched={formik.touched.partner_phone}
                      message={formik.errors.partner_phone}
                    />
                  </div>

                  <div className="form-group col-md-6">
                    <label htmlFor="linkedin_profile">LinkedIn Profile</label>
                    <input
                      type="url"
                      className="form-control"
                      id="linkedin_profile"
                      name="linkedin_profile"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.linkedin_profile}
                    />

                    <FormError
                      touched={formik.touched.linkedin_profile}
                      message={formik.errors.linkedin_profile}
                    />
                  </div>
                </div>

                <div className="form-row justify-content-center">
                  <div className="form-group col-md-12">
                    <label htmlFor="relevant_links">
                      Relevant link(s) to the things you’ve built/designed?
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="relevant_links"
                      name="relevant_links"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.relevant_links}
                    />

                    <FormError
                      touched={formik.touched.relevant_links}
                      message={formik.errors.relevant_links}
                    />
                  </div>
                </div>

                <div className="form-row justify-content-center">
                  {/* <div className="form-group col-md-6">
                    <label htmlFor="job_vacancy_inquiry">
                      How did you find out about this vacancy?
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="job_vacancy_inquiry"
                      name="job_vacancy_inquiry"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.job_vacancy_inquiry}
                    />

                    <FormError
                      touched={formik.touched.job_vacancy_inquiry}
                      message={formik.errors.job_vacancy_inquiry}
                    />
                  </div> */}
                  {/* <div className="form-group col-md-6">
                    <label htmlFor="degree">Degree</label>
                    <select
                      className="form-control"
                      id="degree"
                      name="degree"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.degree}
                    >
                      <option disabled value="DEFAULT">
                        Choose an options below
                      </option>
                      <option value="1">Graduate</option>
                      <option value="2">Bachelor Degree</option>
                      <option value="3">Master Degree</option>
                      <option value="4">Doctoral Degree</option>
                    </select>
                    <FormError
                      touched={formik.touched.degree}
                      message={formik.errors.degree}
                    />
                  </div> */}

                  <div className="form-group col-md-6">
                    <label htmlFor="availability">Availability</label>
                    <input
                      type="date"
                      className="form-control"
                      id="availability"
                      name="availability"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.availability}
                    />

                    <FormError
                      touched={formik.touched.availability}
                      message={formik.errors.availability}
                    />
                  </div>

                  <div className="form-group col-md-6">
                    <label htmlFor="years_of_experience">
                      Years of experience in this role
                    </label>
                    <select
                      className="form-control"
                      id="years_of_experience"
                      name="years_of_experience"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.years_of_experience}
                    >
                      <option
                        label="Choose an option"
                        value="Prefer not to say"
                      >
                        Choose an option
                      </option>
                      <option value="<1">Less than a year</option>
                      <option value="1-3">1-3 years</option>
                      <option value="4-6">4-6 years</option>
                      <option value="7-10">7-10 years</option>
                      <option value="10-14">10-14 years</option>
                      <option value=">15">15 years and above</option>
                      <option value="Prefer not to say">
                        Prefer not to say
                      </option>
                    </select>
                    <FormError
                      touched={formik.touched.years_of_experience}
                      message={formik.errors.years_of_experience}
                    />
                  </div>
                </div>

                <div className="form-row justify-content-center">
                  <div className="form-group col-md-12">
                    <h6>EQUAL OPPORTUNITY MONITORING</h6>
                    <p className="small">
                      This section of the application form will be used for
                      monitoring purposes only. Sparkle recognizes and actively
                      promotes the benefits of a diverse workforce and is
                      committed to treating all employees with dignity and
                      respect regardless of race, gender, disability, age,
                      sexual orientation, religion or belief. We therefore
                      welcome applications from all sections of the community.
                      Note that these questions are optional and you are only
                      encouraged to respond if you are comfortable doing so.
                      Whatever your decision, it will not be considered in the
                      hiring process or thereafter. Any information that you do
                      provide will be recorded and maintained in a confidential
                      file.
                    </p>
                  </div>
                </div>

                <div className="form-row justify-content-center">
                  <div className="form-group col-md-6">
                    <label htmlFor="age">Please select your age range</label>
                    <select
                      className="form-control"
                      id="age"
                      name="age"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.age}
                      required
                    >
                      <option label="Choose Age" value="Prefer not to say">
                        Choose Age
                      </option>
                      <option value="<20">Less than 20</option>
                      <option value="21-25">21-25</option>
                      <option value="26-30">26-30</option>
                      <option value="31-35">31-35</option>
                      <option value="36-40">36-40</option>
                      <option value="41-45">41-45</option>
                      <option value="46-50">46-50</option>
                      <option value="Prefer not to say">
                        Prefer not to say
                      </option>
                    </select>
                    <FormError
                      touched={formik.touched.age}
                      message={formik.errors.age}
                    />
                  </div>

                  <div className="form-group col-md-6">
                    <label htmlFor="gender">What is your gender</label>
                    <select
                      className="form-control"
                      id="gender"
                      name="gender"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.gender}
                    >
                      <option value="Prefer not to say" label="Choose Gender">
                        Choose Gender
                      </option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                      <option value="Prefer not to say">
                        Prefer not to say
                      </option>
                    </select>
                    <FormError
                      touched={formik.touched.gender}
                      message={formik.errors.gender}
                    />
                  </div>
                </div>

                <div className="form-row justify-content-center">
                  <div className="form-group col-md-6">
                    <label htmlFor="disability_inquiry">
                      Do you consider yourself to have a disability
                    </label>
                    <select
                      className="form-control"
                      id="disability_inquiry"
                      name="disability_inquiry"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.disability_inquiry}
                    >
                      <option value="Prefer not to say">
                        Choose an option
                      </option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                      <option value="Prefer not to say">
                        Prefer not to say
                      </option>
                    </select>
                    <FormError
                      touched={formik.touched.disability_inquiry}
                      message={formik.errors.disability_inquiry}
                    />
                  </div>

                  <div className="form-group col-md-6">
                    <label htmlFor="geo_political_zone">
                      Where are you from?
                    </label>
                    <select
                      className="form-control"
                      id="geo_political_zone"
                      name="geo_political_zone"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.geo_political_zone}
                    >
                      <option value="Prefer not to say">Choose Zone</option>
                      <option value="North Central">North Central</option>
                      <option value="North East">North East</option>
                      <option value="North West">North West</option>
                      <option value="South East">South East</option>
                      <option value="South South">South South</option>
                      <option value="South West">South West</option>
                      <option value="Prefer not to say">
                        Prefer not to say
                      </option>
                    </select>
                    <FormError
                      touched={formik.touched.geo_political_zone}
                      message={formik.errors.geo_political_zone}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group col-lg-4">
                    <Form.Group controlId="formFile">
                      <Form.Label>File attachment (PDF)</Form.Label>
                      <Form.Control
                        type="file"
                        name="datas"
                        onChange={handleImage}
                        accept="application/pdf"
                      />
                    </Form.Group>
                  </div>
                </div>

                <div className="form-row justify-content-center">
                  <div className="form-group col-md-12">
                    <div className="">
                      <button
                        type="submit"
                        className="btn sparkle--button__green px-5 mt-4"
                      >
                        {formik.isSubmitting
                          ? "Submiting..."
                          : "Submit Application"}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default ApplicationForm
